// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — MainStreet Operations Store (Zustand)
// Manages status checks, responses, damage reports, resource
// offers, posts, re-entry authorizations & passes — wired to
// Ignatius via the Cloudflare Worker proxy.
// ═══════════════════════════════════════════════════════════════

import { create } from 'zustand';
import { fetchTable, createRecord, updateRecord, deleteRecord, TABLES, FIELDS } from '../lib/api-switch';
import { useAuthStore } from './authStore';
import { logActivity } from '../lib/auditLog';

// ── Types ─────────────────────────────────────────────────────

export interface MSStatusCheck {
  id: number; Rid: number;
  incidentId: string; title: string; question: string;
  targetType: string; targetSectors: string; targetOrgs: string;
  deadline: string; status: string; createdAt: string; authorName: string;
}

export interface MSStatusResponse {
  id: number; Rid: number;
  statusCheckId: string; orgId: string; orgName: string;
  response: string; notes: string; respondedAt: string;
}

export interface MSDamageReport {
  id: number; Rid: number;
  orgId: string; orgName: string; incidentId: string;
  opStatus: string; damageTypes: string; description: string;
  severity: string; reportedBy: string; reportedAt: string; photos: string;
}

export interface MSResourceOffer {
  id: number; Rid: number;
  orgId: string; orgName: string; incidentId: string;
  offerType: string; description: string; quantity: string;
  status: string; offeredBy: string; createdAt: string; notes: string;
}

export interface MSPost {
  id: number; Rid: number;
  incidentId: string; content: string; postType: string;
  priority: string; authorName: string; createdAt: string;
  targetType: string; targetOrgIds: string; targetListId: string;
  parentPostId: string; readBy: string;
}

export interface MSReentryAuth {
  id: number; Rid: number;
  incidentId: string; title: string; targetType: string;
  targetSectors: string; targetOrgs: string;
  issuedBy: string; issuedAt: string; expiresAt: string;
  status: string; notes: string;
}

export interface MSReentryPass {
  id: number; Rid: number;
  authId: string; orgId: string; orgName: string;
  code: string; status: string;
  issuedAt: string; expiresAt: string; verifiedAt: string;
}

// ── Store Interface ───────────────────────────────────────────

interface MainStreetOpsStore {
  // State
  statusChecks: MSStatusCheck[];
  statusResponses: MSStatusResponse[];
  damageReports: MSDamageReport[];
  resourceOffers: MSResourceOffer[];
  posts: MSPost[];
  reentryAuths: MSReentryAuth[];
  reentryPasses: MSReentryPass[];
  loading: boolean;
  error: string | null;

  // Status Checks
  loadStatusChecks: () => Promise<void>;
  createStatusCheck: (data: Partial<MSStatusCheck>) => Promise<MSStatusCheck | null>;
  updateStatusCheck: (id: number, data: Partial<MSStatusCheck>) => Promise<void>;
  deleteStatusCheck: (id: number) => Promise<void>;

  // Status Responses
  loadStatusResponses: (checkId?: string) => Promise<void>;
  submitResponse: (data: Partial<MSStatusResponse>) => Promise<MSStatusResponse | null>;

  // Damage Reports
  loadDamageReports: () => Promise<void>;
  createDamageReport: (data: Partial<MSDamageReport>) => Promise<MSDamageReport | null>;
  updateDamageReport: (id: number, data: Partial<MSDamageReport>) => Promise<void>;
  deleteDamageReport: (id: number) => Promise<void>;

  // Resource Offers
  loadResourceOffers: () => Promise<void>;
  createResourceOffer: (data: Partial<MSResourceOffer>) => Promise<MSResourceOffer | null>;
  updateOfferStatus: (id: number, status: string) => Promise<void>;
  deleteResourceOffer: (id: number) => Promise<void>;

  // Posts
  loadPosts: () => Promise<void>;
  createPost: (data: Partial<MSPost>) => Promise<MSPost | null>;
  deletePost: (id: number) => Promise<void>;

  // Re-entry Authorizations
  loadReentryAuths: () => Promise<void>;
  createReentryAuth: (data: Partial<MSReentryAuth>) => Promise<MSReentryAuth | null>;
  updateReentryAuth: (id: number, data: Partial<MSReentryAuth>) => Promise<void>;

  // Re-entry Passes
  loadReentryPasses: () => Promise<void>;
  issuePass: (data: Partial<MSReentryPass>) => Promise<MSReentryPass | null>;
  bulkIssuePasses: (passes: Array<Partial<MSReentryPass>>, onProgress?: (done: number, total: number) => void) => Promise<number>;
  verifyPass: (code: string) => Promise<MSReentryPass | null>;
  updatePassStatus: (id: number, status: string) => Promise<void>;
}

// ── Helper: get auth token ────────────────────────────────────

function getToken(): string | undefined {
  return useAuthStore.getState().token ?? undefined;
}

// ── Row mappers ───────────────────────────────────────────────

function mapStatusCheck(row: Record<string, unknown>): MSStatusCheck {
  const id = Number(row.id) || 0;
  return {
    id, Rid: id,
    incidentId: (row.incidentId as string) ?? '',
    title: (row.title as string) ?? '',
    question: (row.question as string) ?? '',
    targetType: (row.targetType as string) ?? '',
    targetSectors: (row.targetSectors as string) ?? '',
    targetOrgs: (row.targetOrgs as string) ?? '',
    deadline: (row.deadline as string) ?? '',
    status: (row.status as string) ?? '',
    createdAt: (row.createdAt as string) ?? '',
    authorName: (row.authorName as string) ?? '',
  };
}

function mapStatusResponse(row: Record<string, unknown>): MSStatusResponse {
  const id = Number(row.id) || 0;
  return {
    id, Rid: id,
    statusCheckId: (row.statusCheckId as string) ?? '',
    orgId: (row.orgId as string) ?? '',
    orgName: (row.orgName as string) ?? '',
    response: (row.response as string) ?? '',
    notes: (row.notes as string) ?? '',
    respondedAt: (row.respondedAt as string) ?? '',
  };
}

function mapDamageReport(row: Record<string, unknown>): MSDamageReport {
  const id = Number(row.id) || 0;
  return {
    id, Rid: id,
    orgId: (row.orgId as string) ?? '',
    orgName: (row.orgName as string) ?? '',
    incidentId: (row.incidentId as string) ?? '',
    opStatus: ((row.operationalStatus as string) || (row.opStatus as string)) ?? '',
    damageTypes: (() => {
      const raw = (row.damageTypes as string) ?? '';
      // Normalize JSON array strings like '["roof_damage","water"]' to 'roof_damage,water'
      if (raw.startsWith('[')) {
        try { return (JSON.parse(raw) as string[]).join(','); } catch { /* fall through */ }
      }
      return raw;
    })(),
    description: (row.description as string) ?? '',
    severity: (row.severity as string) ?? '',
    reportedBy: ((row.reportedByName as string) || (row.reportedBy as string)) ?? '',
    reportedAt: (row.reportedAt as string) ?? '',
    photos: (row.photos as string) ?? '',
  };
}

function mapResourceOffer(row: Record<string, unknown>): MSResourceOffer {
  const id = Number(row.id) || 0;
  return {
    id, Rid: id,
    orgId: (row.orgId as string) ?? '',
    orgName: (row.orgName as string) ?? '',
    incidentId: (row.incidentId as string) ?? '',
    offerType: (row.offerType as string) ?? '',
    description: (row.description as string) ?? '',
    quantity: (row.quantity as string) ?? '',
    status: (row.status as string) ?? '',
    offeredBy: ((row.offeredByName as string) || (row.offeredBy as string)) ?? '',
    createdAt: ((row.offerCreatedAt as string) || (row.createdAt as string)) ?? '',
    notes: (row.notes as string) ?? '',
  };
}

function mapPost(row: Record<string, unknown>): MSPost {
  const id = Number(row.id) || 0;
  return {
    id, Rid: id,
    incidentId: (row.incidentId as string) ?? '',
    content: (row.content as string) ?? '',
    postType: (row.postType as string) ?? '',
    priority: (row.priority as string) ?? '',
    authorName: (row.authorName as string) ?? '',
    createdAt: ((row.postCreatedAt as string) || (row.createdAt as string)) ?? '',
    targetType: (row.targetType as string) ?? '',
    targetOrgIds: (row.targetOrgIds as string) ?? '',
    targetListId: (row.targetListId as string) ?? '',
    parentPostId: (row.parentPostId as string) ?? '',
    readBy: (row.readBy as string) ?? '',
  };
}

function mapReentryAuth(row: Record<string, unknown>): MSReentryAuth {
  const id = Number(row.id) || 0;
  return {
    id, Rid: id,
    incidentId: (row.incidentId as string) ?? '',
    title: ((row.authTitle as string) || (row.title as string)) ?? '',
    targetType: (row.targetType as string) ?? '',
    targetSectors: (row.targetSectors as string) ?? '',
    targetOrgs: (row.targetOrgs as string) ?? '',
    issuedBy: ((row.issuedByName as string) || (row.issuedBy as string)) ?? '',
    issuedAt: (row.issuedAt as string) ?? '',
    expiresAt: (row.expiresAt as string) ?? '',
    status: ((row.authStatus as string) || (row.status as string)) ?? '',
    notes: (row.notes as string) ?? '',
  };
}

function mapReentryPass(row: Record<string, unknown>): MSReentryPass {
  const id = Number(row.id) || 0;
  return {
    id, Rid: id,
    authId: (row.authId as string) ?? '',
    orgId: (row.orgId as string) ?? '',
    orgName: ((row.organizationName as string) || (row.orgName as string)) ?? '',
    code: ((row.passCode as string) || (row.code as string)) ?? '',
    status: ((row.passStatus as string) || (row.status as string)) ?? '',
    issuedAt: (row.issuedAt as string) ?? '',
    expiresAt: (row.expiresAt as string) ?? '',
    verifiedAt: (row.verifiedAt as string) ?? '',
  };
}

// ── Store ─────────────────────────────────────────────────────

export const useMainStreetOpsStore = create<MainStreetOpsStore>((set, get) => ({
  statusChecks: [],
  statusResponses: [],
  damageReports: [],
  resourceOffers: [],
  posts: [],
  reentryAuths: [],
  reentryPasses: [],
  loading: false,
  error: null,

  // ── Status Checks ───────────────────────────────────────────

  loadStatusChecks: async () => {
    set({ loading: true, error: null });
    try {
      const data = await fetchTable(TABLES.msStatusChecks, { token: getToken() });
      const statusChecks = data.map(mapStatusCheck);
      set({ statusChecks, loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load status checks' });
    }
  },

  createStatusCheck: async (data) => {
    try {
      const token = getToken();
      const F = FIELDS.msStatusChecks;
      const fields: Array<[string, string]> = [
        [F.incidentId, data.incidentId ?? ''],
        [F.title, data.title ?? ''],
        [F.question, data.question ?? ''],
        [F.targetType, data.targetType ?? ''],
        [F.targetSectors, data.targetSectors ?? ''],
        [F.targetOrgs, data.targetOrgs ?? ''],
        [F.deadline, data.deadline ?? ''],
        [F.status, data.status ?? ''],
        [F.createdAt, data.createdAt ?? ''],
        [F.authorName, data.authorName ?? ''],
      ];
      const rid = await createRecord(TABLES.msStatusChecks, fields, token);
      await get().loadStatusChecks();
      return get().statusChecks.find(sc => sc.id === rid) ?? null;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to create status check' });
      return null;
    }
  },

  updateStatusCheck: async (id, data) => {
    const token = getToken();
    const F = FIELDS.msStatusChecks;
    const fields: Array<[string, string]> = [];
    if (data.incidentId !== undefined) fields.push([F.incidentId, data.incidentId]);
    if (data.title !== undefined) fields.push([F.title, data.title]);
    if (data.question !== undefined) fields.push([F.question, data.question]);
    if (data.targetType !== undefined) fields.push([F.targetType, data.targetType]);
    if (data.targetSectors !== undefined) fields.push([F.targetSectors, data.targetSectors]);
    if (data.targetOrgs !== undefined) fields.push([F.targetOrgs, data.targetOrgs]);
    if (data.deadline !== undefined) fields.push([F.deadline, data.deadline]);
    if (data.status !== undefined) fields.push([F.status, data.status]);
    if (data.createdAt !== undefined) fields.push([F.createdAt, data.createdAt]);
    if (data.authorName !== undefined) fields.push([F.authorName, data.authorName]);
    if (fields.length > 0) {
      await updateRecord(TABLES.msStatusChecks, id, fields, token);
      await get().loadStatusChecks();
    }
  },

  deleteStatusCheck: async (id) => {
    const token = getToken();
    await deleteRecord(TABLES.msStatusChecks, id, token);
    set({ statusChecks: get().statusChecks.filter(sc => sc.id !== id) });
  },

  // ── Status Responses ────────────────────────────────────────

  loadStatusResponses: async (checkId?: string) => {
    set({ loading: true, error: null });
    try {
      const data = await fetchTable(TABLES.msStatusResponses, { token: getToken() });
      let responses = data.map(mapStatusResponse);
      if (checkId) {
        responses = responses.filter(r => r.statusCheckId === checkId);
      }
      set({ statusResponses: responses, loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load status responses' });
    }
  },

  submitResponse: async (data) => {
    try {
      const token = getToken();
      const F = FIELDS.msStatusResponses;
      const fields: Array<[string, string]> = [
        [F.statusCheckId, data.statusCheckId ?? ''],
        [F.orgId, data.orgId ?? ''],
        [F.orgName, data.orgName ?? ''],
        [F.response, data.response ?? ''],
        [F.notes, data.notes ?? ''],
        [F.respondedAt, data.respondedAt ?? ''],
      ];
      const rid = await createRecord(TABLES.msStatusResponses, fields, token);
      await get().loadStatusResponses();
      return get().statusResponses.find(r => r.id === rid) ?? null;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to submit response' });
      return null;
    }
  },

  // ── Damage Reports ──────────────────────────────────────────

  loadDamageReports: async () => {
    set({ loading: true, error: null });
    try {
      const data = await fetchTable(TABLES.msDamageReports, { token: getToken() });
      const damageReports = data.map(mapDamageReport);
      set({ damageReports, loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load damage reports' });
    }
  },

  createDamageReport: async (data) => {
    try {
      const token = getToken();
      const F = FIELDS.msDamageReports;
      const fields: Array<[string, string]> = [
        [F.orgId, data.orgId ?? ''],
        [F.orgName, data.orgName ?? ''],
        [F.incidentId, data.incidentId ?? ''],
        [F.opStatus, data.opStatus ?? ''],
        [F.damageTypes, data.damageTypes ?? ''],
        [F.description, data.description ?? ''],
        [F.severity, data.severity ?? ''],
        [F.reportedBy, data.reportedBy ?? ''],
        [F.reportedAt, data.reportedAt ?? ''],
        [F.photos, data.photos ?? ''],
      ];
      const rid = await createRecord(TABLES.msDamageReports, fields, token);
      await get().loadDamageReports();
      return get().damageReports.find(dr => dr.id === rid) ?? null;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to create damage report' });
      return null;
    }
  },

  updateDamageReport: async (id, data) => {
    const token = getToken();
    const F = FIELDS.msDamageReports;
    const fields: Array<[string, string]> = [];
    if (data.orgId !== undefined) fields.push([F.orgId, data.orgId]);
    if (data.orgName !== undefined) fields.push([F.orgName, data.orgName]);
    if (data.incidentId !== undefined) fields.push([F.incidentId, data.incidentId]);
    if (data.opStatus !== undefined) fields.push([F.opStatus, data.opStatus]);
    if (data.damageTypes !== undefined) fields.push([F.damageTypes, data.damageTypes]);
    if (data.description !== undefined) fields.push([F.description, data.description]);
    if (data.severity !== undefined) fields.push([F.severity, data.severity]);
    if (data.reportedBy !== undefined) fields.push([F.reportedBy, data.reportedBy]);
    if (data.reportedAt !== undefined) fields.push([F.reportedAt, data.reportedAt]);
    if (data.photos !== undefined) fields.push([F.photos, data.photos]);
    if (fields.length > 0) {
      await updateRecord(TABLES.msDamageReports, id, fields, token);
      await get().loadDamageReports();
    }
  },

  deleteDamageReport: async (id) => {
    const token = getToken();
    await deleteRecord(TABLES.msDamageReports, id, token);
    set({ damageReports: get().damageReports.filter(dr => dr.id !== id) });
  },

  // ── Resource Offers ─────────────────────────────────────────

  loadResourceOffers: async () => {
    set({ loading: true, error: null });
    try {
      const data = await fetchTable(TABLES.msResourceOffers, { token: getToken() });
      const resourceOffers = data.map(mapResourceOffer);
      set({ resourceOffers, loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load resource offers' });
    }
  },

  createResourceOffer: async (data) => {
    try {
      const token = getToken();
      const F = FIELDS.msResourceOffers;
      const fields: Array<[string, string]> = [
        [F.orgId, data.orgId ?? ''],
        [F.orgName, data.orgName ?? ''],
        [F.incidentId, data.incidentId ?? ''],
        [F.offerType, data.offerType ?? ''],
        [F.description, data.description ?? ''],
        [F.quantity, data.quantity ?? ''],
        [F.status, data.status ?? ''],
        [F.offeredBy, data.offeredBy ?? ''],
        [F.createdAt, data.createdAt ?? ''],
        [F.notes, data.notes ?? ''],
      ];
      const rid = await createRecord(TABLES.msResourceOffers, fields, token);
      await get().loadResourceOffers();
      return get().resourceOffers.find(ro => ro.id === rid) ?? null;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to create resource offer' });
      return null;
    }
  },

  updateOfferStatus: async (id, status) => {
    const token = getToken();
    const F = FIELDS.msResourceOffers;
    await updateRecord(TABLES.msResourceOffers, id, [[F.status, status]], token);
    await get().loadResourceOffers();
  },

  deleteResourceOffer: async (id) => {
    const token = getToken();
    await deleteRecord(TABLES.msResourceOffers, id, token);
    set({ resourceOffers: get().resourceOffers.filter(ro => ro.id !== id) });
  },

  // ── Posts ───────────────────────────────────────────────────

  loadPosts: async () => {
    set({ loading: true, error: null });
    try {
      const data = await fetchTable(TABLES.msPosts, { token: getToken() });
      const posts = data.map(mapPost);
      set({ posts, loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load posts' });
    }
  },

  createPost: async (data) => {
    try {
      const token = getToken();
      const F = FIELDS.msPosts;
      const fields: Array<[string, string]> = [
        [F.incidentId, data.incidentId ?? ''],
        [F.content, data.content ?? ''],
        [F.postType, data.postType ?? ''],
        [F.priority, data.priority ?? ''],
        [F.authorName, data.authorName ?? ''],
        [F.createdAt, data.createdAt ?? ''],
        [F.targetType, data.targetType ?? ''],
        [F.targetOrgIds, data.targetOrgIds ?? ''],
        [F.targetListId, data.targetListId ?? ''],
        [F.parentPostId, data.parentPostId ?? ''],
      ];
      const rid = await createRecord(TABLES.msPosts, fields, token);
      await get().loadPosts();
      return get().posts.find(p => p.id === rid) ?? null;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to create post' });
      return null;
    }
  },

  deletePost: async (id) => {
    const token = getToken();
    await deleteRecord(TABLES.msPosts, id, token);
    set({ posts: get().posts.filter(p => p.id !== id) });
  },

  // ── Re-entry Authorizations ─────────────────────────────────

  loadReentryAuths: async () => {
    set({ loading: true, error: null });
    try {
      const data = await fetchTable(TABLES.msReentryAuths, { token: getToken() });
      const reentryAuths = data.map(mapReentryAuth);
      set({ reentryAuths, loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load re-entry authorizations' });
    }
  },

  createReentryAuth: async (data) => {
    try {
      const token = getToken();
      const F = FIELDS.msReentryAuths;
      const fields: Array<[string, string]> = [
        [F.incidentId, data.incidentId ?? ''],
        [F.title, data.title ?? ''],
        [F.targetType, data.targetType ?? ''],
        [F.targetSectors, data.targetSectors ?? ''],
        [F.targetOrgs, data.targetOrgs ?? ''],
        [F.issuedBy, data.issuedBy ?? ''],
        [F.issuedAt, data.issuedAt ?? ''],
        [F.expiresAt, data.expiresAt ?? ''],
        [F.status, data.status ?? ''],
        [F.notes, data.notes ?? ''],
      ];
      const rid = await createRecord(TABLES.msReentryAuths, fields, token);
      await get().loadReentryAuths();
      return get().reentryAuths.find(ra => ra.id === rid) ?? null;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to create re-entry authorization' });
      return null;
    }
  },

  updateReentryAuth: async (id, data) => {
    const token = getToken();
    const F = FIELDS.msReentryAuths;
    const fields: Array<[string, string]> = [];
    if (data.incidentId !== undefined) fields.push([F.incidentId, data.incidentId]);
    if (data.title !== undefined) fields.push([F.title, data.title]);
    if (data.targetType !== undefined) fields.push([F.targetType, data.targetType]);
    if (data.targetSectors !== undefined) fields.push([F.targetSectors, data.targetSectors]);
    if (data.targetOrgs !== undefined) fields.push([F.targetOrgs, data.targetOrgs]);
    if (data.issuedBy !== undefined) fields.push([F.issuedBy, data.issuedBy]);
    if (data.issuedAt !== undefined) fields.push([F.issuedAt, data.issuedAt]);
    if (data.expiresAt !== undefined) fields.push([F.expiresAt, data.expiresAt]);
    if (data.status !== undefined) fields.push([F.status, data.status]);
    if (data.notes !== undefined) fields.push([F.notes, data.notes]);
    if (fields.length > 0) {
      await updateRecord(TABLES.msReentryAuths, id, fields, token);
      await get().loadReentryAuths();
    }
  },

  // ── Re-entry Passes ─────────────────────────────────────────

  loadReentryPasses: async () => {
    set({ loading: true, error: null });
    try {
      const data = await fetchTable(TABLES.msReentryPasses, { token: getToken() });
      const reentryPasses = data.map(mapReentryPass);
      set({ reentryPasses, loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load re-entry passes' });
    }
  },

  issuePass: async (data) => {
    try {
      const token = getToken();
      const F = FIELDS.msReentryPasses;
      const fields: Array<[string, string]> = [
        [F.authId, data.authId ?? ''],
        [F.orgId, data.orgId ?? ''],
        [F.orgName, data.orgName ?? ''],
        [F.code, data.code ?? ''],
        [F.status, data.status ?? ''],
        [F.issuedAt, data.issuedAt ?? ''],
        [F.expiresAt, data.expiresAt ?? ''],
        [F.verifiedAt, data.verifiedAt ?? ''],
      ];
      const rid = await createRecord(TABLES.msReentryPasses, fields, token);
      await get().loadReentryPasses();

      try {
        const { useWorkflowStore } = await import('./workflowStore');
        await useWorkflowStore.getState().checkAndTriggerWorkflows(
          'reentry_requested', 'mainstreet', TABLES.msReentryPasses,
          String(rid), `${data.orgName ?? 'Pass'} — ${data.code ?? ''}`,
          useAuthStore.getState().user?.email || 'system', undefined,
          data as Record<string, unknown>,
        );
      } catch (e) { console.warn('[MainStreet] Workflow trigger failed:', e); }

      logActivity('mainstreet', 'create', 'reentry_pass', String(rid), `Pass issued: ${data.code || ''} to ${data.orgName || ''}`);
      return get().reentryPasses.find(rp => rp.id === rid) ?? null;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to issue pass' });
      return null;
    }
  },

  bulkIssuePasses: async (passes, onProgress) => {
    const token = getToken();
    const F = FIELDS.msReentryPasses;
    const BATCH_SIZE = 8;
    let issued = 0;

    for (let i = 0; i < passes.length; i += BATCH_SIZE) {
      const batch = passes.slice(i, i + BATCH_SIZE);
      await Promise.all(batch.map(async (data) => {
        try {
          const fields: Array<[string, string]> = [
            [F.authId, data.authId ?? ''],
            [F.orgId, data.orgId ?? ''],
            [F.orgName, data.orgName ?? ''],
            [F.code, data.code ?? ''],
            [F.status, data.status ?? 'issued'],
            [F.issuedAt, data.issuedAt ?? ''],
            [F.expiresAt, data.expiresAt ?? ''],
            [F.verifiedAt, data.verifiedAt ?? ''],
          ];
          await createRecord(TABLES.msReentryPasses, fields, token);
          issued++;
        } catch { /* skip failed */ }
      }));
      onProgress?.(Math.min(i + BATCH_SIZE, passes.length), passes.length);
    }

    // Single reload at end
    await get().loadReentryPasses();
    return issued;
  },

  verifyPass: async (code: string) => {
    // Load all passes and find by code match
    await get().loadReentryPasses();
    const match = get().reentryPasses.find(rp => rp.code === code) ?? null;
    if (match) {
      // Mark as verified
      const token = getToken();
      const F = FIELDS.msReentryPasses;
      await updateRecord(TABLES.msReentryPasses, match.id, [
        [F.verifiedAt, new Date().toISOString()],
      ], token);
      await get().loadReentryPasses();
      return get().reentryPasses.find(rp => rp.id === match.id) ?? null;
    }
    return null;
  },

  updatePassStatus: async (id, status) => {
    const token = getToken();
    const F = FIELDS.msReentryPasses;
    const pass = get().reentryPasses.find(p => p.id === id);
    await updateRecord(TABLES.msReentryPasses, id, [[F.status, status]], token);
    await get().loadReentryPasses();
    logActivity('mainstreet', status === 'revoked' ? 'revoke' : status === 'issued' ? 'reinstate' : 'update', 'reentry_pass', String(id), `Pass ${pass?.code || ''} ${status} — ${pass?.orgName || ''}`);
  },
}));
